import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  Users, User, MessageSquare, BedDouble, Mail, Phone,
  ChevronLeft, ChevronRight, CheckCircle2, Sparkles,
} from 'lucide-react';
import Modal from './Modal';
import { CREATE_CLIENT } from '../graphql/admin/mutations/client';
import { CREATE_BOOKING } from '../graphql/mutations/booking';
import type { PackageDetails } from '../types/package';
import type { Formula } from '../types/package';

const TYPES_CHAMBRE = [
  { value: 'single', label: 'Single', capacity: 1 },
  { value: 'double', label: 'Double', capacity: 2 },
  { value: 'triple', label: 'Triple', capacity: 3 },
  { value: 'quadruple', label: 'Quadruple', capacity: 4 },
  { value: 'quintuple', label: 'Quintuple', capacity: 5 },
] as const;

const CAPACITY_BY_TYPE: Record<string, number> = TYPES_CHAMBRE.reduce(
  (acc, t) => ({ ...acc, [t.value]: t.capacity }),
  {} as Record<string, number>
);

type Personne = { type: 'adulte' | 'enfant'; nom: string; prenom: string; chambre: number };
type Bebe = { nom: string; prenom: string; chambre: number };
type Chambre = { type: string };

type FormState = {
  nbrAdulte: number;
  nbrEnfantAvecLit: number;
  nbrEnfantSansLit: number;
  /** Total enfants (computed: nbrEnfantAvecLit + nbrEnfantSansLit). Conservé pour l'envoi backend. */
  nbrEnfant: number;
  nbrBebe: number;
  nbrChambre: number;
  telephone: string;
  mail: string;
  formuleId: number;
  personnes: Personne[];
  bebes: Bebe[];
  chambres: Chambre[];
  paiement: string;
  montantAvance: string;
  montantPaye: string;
  statutDisponibilite: string;
  typeReservation: string;
  specialRequests: string;
};

type Theme = { primaryColor: string; secondaryColor: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  pkg: PackageDetails;
  selectedFormula: Formula | null;
  theme: Theme;
};

type Step = 'travel' | 'contact';

function buildInitialForm(formulaId: number, defaultRoomType: string): FormState {
  return {
    nbrAdulte: 1,
    nbrEnfantAvecLit: 0,
    nbrEnfantSansLit: 0,
    nbrEnfant: 0,
    nbrBebe: 0,
    nbrChambre: 1,
    telephone: '',
    mail: '',
    formuleId: formulaId,
    personnes: [{ type: 'adulte', nom: '', prenom: '', chambre: 1 }],
    bebes: [],
    chambres: [{ type: defaultRoomType }],
    paiement: 'non_paye',
    montantAvance: '',
    montantPaye: '',
    statutDisponibilite: 'attente',
    typeReservation: 'enligne',
    specialRequests: '',
  };
}

export default function ClientBookingFormModal({
  isOpen,
  onClose,
  pkg,
  selectedFormula,
  theme,
}: Props) {
  const [form, setForm] = useState<FormState | null>(null);
  const [step, setStep] = useState<Step>('travel');
  const [done, setDone] = useState(false);
  const [submittedBookingId, setSubmittedBookingId] = useState<number | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const [createClient, { loading: loadingClient }] = useMutation(CREATE_CLIENT);
  const [createBooking, { loading: loadingBooking }] = useMutation(CREATE_BOOKING);

  const agencyId = pkg.agency?.id;

  /**
   * Types de chambre proposés par la formule sélectionnée.
   * - Mode PER_ROOM : ne garder que ceux avec un prix > 0 (les autres
   *   sont considérés comme non disponibles par l'agence).
   * - Mode PER_PERSON : tous les types restent disponibles puisque
   *   le tarif ne dépend pas du type de chambre, on filtre néanmoins
   *   ceux dont l'agence a explicitement mis le prix à 0 si la liste
   *   contient des valeurs.
   */
  const availableRoomTypes = useMemo(() => {
    if (!selectedFormula) return TYPES_CHAMBRE.slice();
    const prices = selectedFormula.roomPrices || ({} as Record<string, number>);
    const roomKeys = TYPES_CHAMBRE.map((t) => t.value);
    const hasAnyRoomPrice = roomKeys.some((k) => Number((prices as any)[k]) > 0);
    if (!hasAnyRoomPrice) return TYPES_CHAMBRE.slice();
    return TYPES_CHAMBRE.filter((t) => Number((prices as any)[t.value] || 0) > 0);
  }, [selectedFormula]);

  /** Disponibilités des tarifs spéciaux pour cette formule (basé sur > 0) */
  const childWithBedAvailable = useMemo(() => {
    return Number((selectedFormula?.roomPrices as any)?.childWithBed || 0) > 0;
  }, [selectedFormula]);
  const childWithoutBedAvailable = useMemo(() => {
    return Number((selectedFormula?.roomPrices as any)?.childWithoutBed || 0) > 0;
  }, [selectedFormula]);
  const babyAvailable = useMemo(() => {
    return Number((selectedFormula?.roomPrices as any)?.baby || 0) > 0;
  }, [selectedFormula]);

  useEffect(() => {
    if (isOpen && selectedFormula?.id) {
      const defaultType = availableRoomTypes[0]?.value || 'single';
      setForm(buildInitialForm(selectedFormula.id, defaultType));
      setStep('travel');
      setDone(false);
      setSubmittedBookingId(null);
      setSubmitErr(null);
      setTouched(false);
    }
  }, [isOpen, selectedFormula?.id, availableRoomTypes]);

  /**
   * Met à jour les voyageurs en gardant la cohérence des tableaux `personnes`
   * et `bebes`. Distingue désormais enfants avec lit / sans lit pour permettre
   * une tarification différenciée et la validation de la capacité.
   */
  const updatePersonnesEtBebes = useCallback(
    (adulteCount: number, enfantAvecLit: number, enfantSansLit: number, bebeCount: number) => {
      setForm((f) => {
        if (!f) return f;
        const enfantTotal = enfantAvecLit + enfantSansLit;
        const total = adulteCount + enfantTotal;
        let personnes = [...f.personnes];
        if (personnes.length < total) {
          for (let i = personnes.length; i < total; i++) {
            personnes.push({
              type: i < adulteCount ? 'adulte' : 'enfant',
              nom: '',
              prenom: '',
              chambre: 1,
            });
          }
        } else if (personnes.length > total) {
          personnes = personnes.slice(0, total);
        }
        personnes = personnes.map((p, idx) => ({
          ...p,
          type: idx < adulteCount ? 'adulte' : 'enfant',
          chambre: p.chambre > f.nbrChambre ? 1 : p.chambre || 1,
        }));
        let bebes = [...(f.bebes || [])];
        if (bebes.length < bebeCount) {
          for (let i = bebes.length; i < bebeCount; i++) {
            bebes.push({ nom: '', prenom: '', chambre: 1 });
          }
        } else if (bebes.length > bebeCount) {
          bebes = bebes.slice(0, bebeCount);
        }
        bebes = bebes.map((b) => ({
          ...b,
          chambre: b.chambre > f.nbrChambre ? 1 : b.chambre || 1,
        }));
        return {
          ...f,
          personnes,
          nbrAdulte: adulteCount,
          nbrEnfantAvecLit: enfantAvecLit,
          nbrEnfantSansLit: enfantSansLit,
          nbrEnfant: enfantTotal,
          nbrBebe: bebeCount,
          bebes,
        };
      });
    },
    []
  );

  /**
   * Définit le nombre de chambres par type (par exemple { single: 1, double: 1 })
   * et reconstruit le tableau `chambres` dans l'ordre canonique
   * (single → double → triple → quadruple → quintuple).
   */
  const setRoomsByType = useCallback(
    (counts: Record<string, number>) => {
      setForm((f) => {
        if (!f) return f;
        const chambres: Chambre[] = [];
        for (const t of TYPES_CHAMBRE) {
          const n = Math.max(0, counts[t.value] || 0);
          for (let i = 0; i < n; i++) {
            chambres.push({ type: t.value });
          }
        }
        const nbrChambre = chambres.length;
        // Réaffecter les voyageurs si leur chambre n'existe plus
        const personnes = f.personnes.map((p) => ({
          ...p,
          chambre: p.chambre > nbrChambre ? 1 : p.chambre || 1,
        }));
        const bebes = f.bebes.map((b) => ({
          ...b,
          chambre: b.chambre > nbrChambre ? 1 : b.chambre || 1,
        }));
        return {
          ...f,
          chambres,
          nbrChambre,
          personnes,
          bebes,
        };
      });
    },
    []
  );

  /** Nombre de chambres regroupé par type (calcul dérivé de `chambres`) */
  const roomCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of TYPES_CHAMBRE) counts[t.value] = 0;
    (form?.chambres || []).forEach((c) => {
      counts[c.type] = (counts[c.type] || 0) + 1;
    });
    return counts;
  }, [form?.chambres]);

  /** Capacité totale de l'ensemble des chambres */
  const totalCapacity = useMemo(() => {
    return (form?.chambres || []).reduce((sum, c) => sum + (CAPACITY_BY_TYPE[c.type] || 0), 0);
  }, [form?.chambres]);

  /**
   * Nombre de voyageurs occupant un lit (adultes + enfants AVEC lit).
   * Les enfants sans lit et les bébés ne comptent pas pour la capacité.
   */
  const totalPeople =
    (form?.nbrAdulte || 0) + (form?.nbrEnfantAvecLit || 0);
  const roomConsistency =
    totalCapacity === totalPeople
      ? 'ok'
      : totalCapacity < totalPeople
        ? 'undersized'
        : 'oversized';

  const handlePersonneChange = (idx: number, field: keyof Personne, value: string | number) => {
    setForm((f) => {
      if (!f) return f;
      const personnes = [...f.personnes];
      personnes[idx] = { ...personnes[idx], [field]: value };
      return { ...f, personnes };
    });
  };

  const handleBebeChange = (idx: number, field: keyof Bebe, value: string | number) => {
    setForm((f) => {
      if (!f) return f;
      const bebes = [...(f.bebes || [])];
      bebes[idx] = { ...bebes[idx], [field]: value };
      return { ...f, bebes };
    });
  };

  const setField = (name: keyof FormState, value: any) => {
    setForm((f) => (f ? { ...f, [name]: value } : f));
  };

  const calculateTotalPrice = (): number => {
    if (!pkg || !form) return 0;
    const fId = Number(form.formuleId);
    const selectedF = pkg.formulas?.find((x) => x.id === fId);
    if (!selectedF) return 0;

    // Mode legacy : prix forfaitaire par personne (anciens packages)
    if (selectedF.priceType === 'PER_PERSON' && selectedF.perPersonPrice) {
      return selectedF.perPersonPrice * (form.nbrAdulte + form.nbrEnfant);
    }

    const prices: any = selectedF.roomPrices || {};
    let total = 0;

    // Adultes : prix par personne pour le type de leur chambre
    for (let i = 0; i < form.nbrAdulte; i++) {
      const person = form.personnes[i];
      const room = person ? form.chambres[(person.chambre || 1) - 1] : form.chambres[0];
      const key = room?.type as keyof typeof prices;
      total += Number(prices[key]) || 0;
    }

    // Enfants avec lit : prix dédié (par enfant)
    total += (Number(prices.childWithBed) || 0) * (form.nbrEnfantAvecLit || 0);

    // Enfants sans lit : prix dédié (par enfant)
    total += (Number(prices.childWithoutBed) || 0) * (form.nbrEnfantSansLit || 0);

    // Bébés : prix dédié (par bébé)
    total += (Number(prices.baby) || 0) * (form.nbrBebe || 0);

    return total;
  };

  const totalPrice = useMemo(() => calculateTotalPrice(), [form, pkg]);

  // ─── Validations par étape ───────────────────────────────────────────────
  const travelStepValid = useMemo(() => {
    if (!form) return false;
    if (form.nbrAdulte < 1) return false;
    if (form.nbrChambre < 1) return false;
    // Au moins le nom + prénom du voyageur principal
    const main = form.personnes[0];
    if (!main?.nom?.trim() || !main?.prenom?.trim()) return false;
    // La capacité totale des chambres doit couvrir les adultes + enfants.
    if (roomConsistency === 'undersized') return false;
    return true;
  }, [form, roomConsistency]);

  const contactStepValid = useMemo(() => {
    if (!form) return false;
    if (!form.mail?.trim()) return false;
    if (!form.telephone?.trim()) return false;
    return true;
  }, [form]);

  const handleNext = () => {
    setTouched(true);
    if (travelStepValid) {
      setStep('contact');
      setTouched(false);
    }
  };

  const handleBack = () => {
    setStep('travel');
    setSubmitErr(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErr(null);
    setTouched(true);
    if (!form || !agencyId || !selectedFormula?.id) {
      setSubmitErr('Données incomplètes.');
      return;
    }
    if (!travelStepValid) {
      setSubmitErr('Complétez l\'étape précédente avant de continuer.');
      setStep('travel');
      return;
    }
    if (!contactStepValid) {
      setSubmitErr('Email et téléphone sont obligatoires.');
      return;
    }

    const mainPerson = form.personnes[0];
    try {
      const { data: clientData } = await createClient({
        variables: {
          input: {
            firstName: mainPerson.nom,
            lastName: mainPerson.prenom,
            email: form.mail.trim(),
            phone: form.telephone.trim(),
            address: '',
            agencyId,
          },
        },
      });
      const clientId = clientData?.createClient?.id;
      if (!clientId) {
        setSubmitErr("Impossible d'enregistrer vos coordonnées. Réessayez plus tard.");
        return;
      }

      const {
        formuleId: fid,
        specialRequests: sr,
        montantAvance: maStr,
        montantPaye: mpStr,
        ...bookingRest
      } = form;
      const input = {
        ...bookingRest,
        packageId: pkg.id,
        formulaId: Number(fid),
        travelerCount: form.nbrAdulte + form.nbrEnfant + form.nbrBebe,
        totalPrice,
        contactInfo: {
          nom: mainPerson.nom,
          prenom: mainPerson.prenom,
          telephone: form.telephone,
          mail: form.mail,
        },
        clientId,
        nom: mainPerson.nom,
        prenom: mainPerson.prenom,
        montantAvance: maStr && maStr !== '' ? Number(maStr) : null,
        montantPaye: mpStr && mpStr !== '' ? Number(mpStr) : null,
        specialRequests: sr?.trim() || null,
        status: 'PENDING',
        statutReservation: 'en attente',
      };

      const { data: bookingRes } = await createBooking({ variables: { input } });
      const newId = bookingRes?.createBooking?.id;
      if (typeof newId === 'number') setSubmittedBookingId(newId);
      setDone(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erreur lors de l'envoi.";
      setSubmitErr(msg);
    }
  };

  const loading = loadingClient || loadingBooking;

  if (!selectedFormula?.id || !form) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      wide
      title="Demande de réservation"
    >
      {done ? (
        <SuccessView
          bookingId={submittedBookingId}
          onClose={onClose}
          theme={theme}
        />
      ) : (
        <>
          {/* Récap haut */}
          <div className="mb-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-0.5">
                  Votre demande
                </div>
                <div className="text-sm font-semibold text-gray-900 truncate">{pkg.title}</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  Formule : <span className="text-gray-800">{selectedFormula.name}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                  Total estimé
                </div>
                <div className="text-xl font-extrabold tabular-nums" style={{ color: theme.primaryColor }}>
                  {totalPrice.toLocaleString('fr-DZ')} DA
                </div>
              </div>
            </div>
          </div>

          {/* Stepper */}
          <Stepper step={step} primaryColor={theme.primaryColor} travelValid={travelStepValid} />

          {/* Erreur globale */}
          {submitErr && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {submitErr}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 'travel' && (
              <>
                {/* Étape 1.a — Voyageurs */}
                <Section icon={Users} title="Combien êtes-vous ?">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Counter
                      label="Adultes"
                      value={form.nbrAdulte}
                      min={1}
                      onChange={(v) =>
                        updatePersonnesEtBebes(v, form.nbrEnfantAvecLit, form.nbrEnfantSansLit, form.nbrBebe)
                      }
                      accent={theme.primaryColor}
                    />
                    <Counter
                      label="Enfant avec lit"
                      value={form.nbrEnfantAvecLit}
                      min={0}
                      disabled={!childWithBedAvailable}
                      hint={!childWithBedAvailable ? 'Non disponible' : undefined}
                      onChange={(v) =>
                        updatePersonnesEtBebes(form.nbrAdulte, v, form.nbrEnfantSansLit, form.nbrBebe)
                      }
                      accent={theme.primaryColor}
                    />
                    <Counter
                      label="Enfant sans lit"
                      value={form.nbrEnfantSansLit}
                      min={0}
                      disabled={!childWithoutBedAvailable}
                      hint={!childWithoutBedAvailable ? 'Non disponible' : undefined}
                      onChange={(v) =>
                        updatePersonnesEtBebes(form.nbrAdulte, form.nbrEnfantAvecLit, v, form.nbrBebe)
                      }
                      accent={theme.primaryColor}
                    />
                    <Counter
                      label="Bébés"
                      value={form.nbrBebe}
                      min={0}
                      disabled={!babyAvailable}
                      hint={!babyAvailable ? 'Non disponible' : undefined}
                      onChange={(v) =>
                        updatePersonnesEtBebes(form.nbrAdulte, form.nbrEnfantAvecLit, form.nbrEnfantSansLit, v)
                      }
                      accent={theme.primaryColor}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Seuls les adultes et les enfants <strong>avec lit</strong> comptent dans la capacité des chambres.
                  </p>
                </Section>

                {/* Étape 1.b — Chambres par type */}
                <Section
                  icon={BedDouble}
                  title="Quelles chambres souhaitez-vous ?"
                  subtitle="Ex : 1 Single + 1 Double"
                >
                  {availableRoomTypes.length === 0 ? (
                    <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-600 text-center">
                      Aucun type de chambre disponible pour cette formule. Contactez l'agence.
                    </div>
                  ) : (
                    <div className={`grid gap-3 ${availableRoomTypes.length >= 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
                      {availableRoomTypes.map((t) => (
                        <Counter
                          key={t.value}
                          label={`${t.label} (${t.capacity}p)`}
                          value={roomCounts[t.value] || 0}
                          min={0}
                          onChange={(v) =>
                            setRoomsByType({ ...roomCounts, [t.value]: v })
                          }
                          accent={theme.primaryColor}
                        />
                      ))}
                    </div>
                  )}

                  {/* Indicateur de cohérence capacité ↔ voyageurs */}
                  <ConsistencyBanner
                    totalCapacity={totalCapacity}
                    totalPeople={totalPeople}
                    consistency={roomConsistency}
                    primaryColor={theme.primaryColor}
                  />
                </Section>

                {/* Identité des voyageurs */}
                <Section icon={User} title="Voyageurs (nom & prénom)">
                  <p className="text-xs text-gray-500 mb-3">
                    Le premier voyageur sera le contact principal de la réservation.
                  </p>
                  <div className="space-y-2">
                    {form.personnes.map((p, idx) => {
                      const isAdult = p.type === 'adulte';
                      const isMain = idx === 0;
                      return (
                        <div
                          key={idx}
                          className={`rounded-lg p-3 ${
                            isMain ? 'border-2' : 'border border-gray-200 bg-white'
                          }`}
                          style={
                            isMain
                              ? { borderColor: theme.primaryColor, backgroundColor: `${theme.primaryColor}08` }
                              : undefined
                          }
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700">
                              {isAdult ? `Adulte ${idx + 1}` : `Enfant ${idx + 1 - form.nbrAdulte}`}
                              {isMain && (
                                <span
                                  className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white"
                                  style={{ backgroundColor: theme.primaryColor }}
                                >
                                  Principal
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="Nom"
                              value={p.nom}
                              onChange={(e) => handlePersonneChange(idx, 'nom', e.target.value)}
                              className={`rounded-lg border px-3 py-2 text-sm ${
                                touched && isMain && !p.nom.trim() ? 'border-red-300' : 'border-gray-300'
                              }`}
                              required={isMain}
                            />
                            <input
                              type="text"
                              placeholder="Prénom"
                              value={p.prenom}
                              onChange={(e) => handlePersonneChange(idx, 'prenom', e.target.value)}
                              className={`rounded-lg border px-3 py-2 text-sm ${
                                touched && isMain && !p.prenom.trim() ? 'border-red-300' : 'border-gray-300'
                              }`}
                              required={isMain}
                            />
                            {form.nbrChambre > 1 && (
                              <select
                                value={p.chambre}
                                onChange={(e) =>
                                  handlePersonneChange(idx, 'chambre', parseInt(e.target.value, 10))
                                }
                                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              >
                                {form.chambres.map((ch, cidx) => {
                                  const meta = TYPES_CHAMBRE.find((t) => t.value === ch.type);
                                  return (
                                    <option key={cidx} value={cidx + 1}>
                                      Chambre {cidx + 1}{meta ? ` (${meta.label})` : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {form.bebes.length > 0 && (
                      <div className="space-y-2 pt-1">
                        <p className="text-xs font-semibold text-gray-700">Bébés</p>
                        {form.bebes.map((b, idx) => (
                          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-white p-3">
                            <input
                              type="text"
                              placeholder={`Bébé ${idx + 1} — Nom`}
                              value={b.nom}
                              onChange={(e) => handleBebeChange(idx, 'nom', e.target.value)}
                              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                            <input
                              type="text"
                              placeholder={`Bébé ${idx + 1} — Prénom`}
                              value={b.prenom}
                              onChange={(e) => handleBebeChange(idx, 'prenom', e.target.value)}
                              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Section>
              </>
            )}

            {step === 'contact' && (
              <>
                <Section icon={Mail} title="Vos coordonnées de contact">
                  <p className="text-xs text-gray-500 mb-3">
                    L'agence {pkg.agency?.name} vous recontactera sur ces coordonnées.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FieldWithIcon
                      icon={Phone}
                      type="tel"
                      label="Téléphone"
                      required
                      value={form.telephone}
                      onChange={(v) => setField('telephone', v)}
                      autoComplete="tel"
                      error={touched && !form.telephone.trim()}
                    />
                    <FieldWithIcon
                      icon={Mail}
                      type="email"
                      label="Email"
                      required
                      value={form.mail}
                      onChange={(v) => setField('mail', v)}
                      autoComplete="email"
                      error={touched && !form.mail.trim()}
                    />
                  </div>
                </Section>

                <Section icon={MessageSquare} title="Un message pour l'agence ?" subtitle="Optionnel">
                  <textarea
                    value={form.specialRequests}
                    onChange={(e) => setField('specialRequests', e.target.value)}
                    rows={3}
                    placeholder="Demandes particulières, régime alimentaire, flexibilité des dates…"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </Section>

                {/* Résumé voyageurs */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                  <div className="font-semibold text-gray-700 mb-1">Récapitulatif rapide</div>
                  <div>
                    {form.nbrAdulte} adulte{form.nbrAdulte > 1 ? 's' : ''}
                    {form.nbrEnfantAvecLit > 0 && `, ${form.nbrEnfantAvecLit} enfant${form.nbrEnfantAvecLit > 1 ? 's' : ''} avec lit`}
                    {form.nbrEnfantSansLit > 0 && `, ${form.nbrEnfantSansLit} enfant${form.nbrEnfantSansLit > 1 ? 's' : ''} sans lit`}
                    {form.nbrBebe > 0 && `, ${form.nbrBebe} bébé${form.nbrBebe > 1 ? 's' : ''}`}
                    {' • '}
                    {form.nbrChambre} chambre{form.nbrChambre > 1 ? 's' : ''}
                  </div>
                  <div className="mt-0.5">
                    Contact principal : {form.personnes[0]?.prenom} {form.personnes[0]?.nom}
                  </div>
                </div>
              </>
            )}

            {/* Footer actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t border-gray-200">
              {step === 'travel' ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour
                </button>
              )}

              {step === 'travel' ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!travelStepValid}
                  style={{
                    background: travelStepValid
                      ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor || theme.primaryColor})`
                      : undefined,
                  }}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg px-6 py-3 text-sm font-bold text-white shadow-md disabled:opacity-40 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Continuer
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !contactStepValid}
                  style={{
                    background: contactStepValid
                      ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor || theme.primaryColor})`
                      : undefined,
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold text-white shadow-md disabled:opacity-50 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Envoyer ma demande
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </>
      )}
    </Modal>
  );
}

// ─── UI helpers ────────────────────────────────────────────────────────────

function Stepper({
  step,
  primaryColor,
  travelValid,
}: {
  step: Step;
  primaryColor: string;
  travelValid: boolean;
}) {
  const steps: { key: Step; label: string }[] = [
    { key: 'travel', label: 'Votre voyage' },
    { key: 'contact', label: 'Vos informations' },
  ];
  return (
    <div className="flex items-center gap-2 mb-5">
      {steps.map((s, idx) => {
        const active = step === s.key;
        const completed = idx === 0 && step === 'contact';
        return (
          <div key={s.key} className="flex items-center gap-2 flex-1">
            <div
              className="flex items-center gap-2 flex-1"
              style={{ color: active || completed ? primaryColor : '#9ca3af' }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors shrink-0"
                style={{
                  borderColor: active || completed ? primaryColor : '#d1d5db',
                  backgroundColor: completed ? primaryColor : 'white',
                  color: completed ? 'white' : active || completed ? primaryColor : '#9ca3af',
                }}
              >
                {completed ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
              </div>
              <span className={`text-sm font-semibold ${active ? '' : 'hidden sm:inline'}`}>{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className="hidden sm:block flex-1 h-0.5 mx-1"
                style={{ backgroundColor: completed ? primaryColor : '#e5e7eb' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: any;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-gray-700" />
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {subtitle && (
          <span className="text-xs text-gray-500 ml-auto">{subtitle}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function Counter({
  label,
  value,
  min,
  onChange,
  accent,
  disabled,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
  accent: string;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-3 ${
        disabled ? 'opacity-50' : ''
      }`}
      title={disabled ? hint : undefined}
    >
      <div className="text-xs font-medium text-gray-500 mb-2 leading-tight">{label}</div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => !disabled && onChange(Math.max(min, value - 1))}
          disabled={disabled || value <= min}
          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-lg font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          −
        </button>
        <span className="text-xl font-bold tabular-nums" style={{ color: accent }}>
          {value}
        </span>
        <button
          type="button"
          onClick={() => !disabled && onChange(value + 1)}
          disabled={disabled}
          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-lg font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>
      {hint && disabled && (
        <div className="text-[10px] text-gray-400 mt-1 text-center">{hint}</div>
      )}
    </div>
  );
}

function FieldWithIcon({
  icon: Icon,
  type = 'text',
  label,
  value,
  onChange,
  required,
  autoComplete,
  error,
}: {
  icon: any;
  type?: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: string;
  error?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm ${
            error ? 'border-red-300' : 'border-gray-300'
          }`}
          required={required}
        />
      </div>
    </div>
  );
}

function ConsistencyBanner({
  totalCapacity,
  totalPeople,
  consistency,
  primaryColor,
}: {
  totalCapacity: number;
  totalPeople: number;
  consistency: 'ok' | 'undersized' | 'oversized';
  primaryColor: string;
}) {
  if (totalCapacity === 0) {
    return (
      <div className="mt-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-600 text-center">
        Choisissez au moins une chambre pour continuer.
      </div>
    );
  }

  if (consistency === 'undersized') {
    return (
      <div className="mt-4 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-3 text-sm">
        <div className="font-semibold text-red-700">
          Capacité insuffisante : {totalCapacity} place{totalCapacity > 1 ? 's' : ''} pour {totalPeople} voyageur{totalPeople > 1 ? 's' : ''}
        </div>
        <div className="text-xs text-red-600 mt-0.5">
          Ajoutez des chambres (ou augmentez leur taille) pour héberger tout le monde.
        </div>
      </div>
    );
  }

  if (consistency === 'oversized') {
    return (
      <div className="mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-3 text-sm">
        <div className="font-semibold text-amber-800">
          Capacité plus grande que nécessaire : {totalCapacity} places pour {totalPeople} voyageur{totalPeople > 1 ? 's' : ''}
        </div>
        <div className="text-xs text-amber-700 mt-0.5">
          Pas de blocage, mais vous payerez peut-être pour des places non utilisées.
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-lg border-2 px-3 py-3 text-sm font-semibold flex items-center gap-2"
      style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}10`, color: primaryColor }}
    >
      <CheckCircle2 className="w-4 h-4" />
      Configuration cohérente : {totalCapacity} place{totalCapacity > 1 ? 's' : ''} pour {totalPeople} voyageur{totalPeople > 1 ? 's' : ''}
    </div>
  );
}

function SuccessView({
  bookingId,
  onClose,
  theme,
}: {
  bookingId: number | null;
  onClose: () => void;
  theme: Theme;
}) {
  return (
    <div className="text-center py-6">
      <div
        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
        style={{ backgroundColor: `${theme.primaryColor}15` }}
      >
        <CheckCircle2 className="w-9 h-9" style={{ color: theme.primaryColor }} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
      <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
        Merci, votre demande a bien été transmise à l'agence. Vous serez recontacté(e) très
        prochainement par email ou téléphone.
      </p>
      {bookingId != null && (
        <div className="inline-block rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 mb-5">
          <span className="text-xs text-gray-500">Votre référence : </span>
          <span className="font-mono font-bold text-gray-900">#{bookingId}</span>
        </div>
      )}
      <p className="text-xs text-gray-500 mb-5">
        Pour suivre ou annuler votre demande, allez dans <strong>Ma réservation</strong>{' '}
        sur cette page (utilisez le même email).
      </p>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white"
        style={{ backgroundColor: theme.primaryColor }}
      >
        Fermer
      </button>
    </div>
  );
}
